import { createPatch, diffLines } from "diff";
import {
  ArrowLeft,
  ArrowUp,
  Check,
  ChevronDown,
  ChevronRight,
  Plus,
  Eye,
  EyeOff,
  File as FileIcon,
  FileDiff,
  MessageSquarePlus,
  Paperclip,
  Search,
  Square,
  TextSelect,
  Undo2,
  X,
} from "lucide-react";
import { useMemo, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from "react";
import { create } from "zustand";
import type { AiProvider } from "@/desktop/ui";
import { cn } from "@/lib/utils";
import { activeEditor } from "@/workspace/active-editor";
import { ProviderMark } from "@/workspace/chat-header";
import { useTree } from "@/workspace/context";
import { activeWorkspace, readFile } from "@/workspace/file-ops";
import { openAt } from "@/workspace/search-panel";
import { useWorkspace } from "@/workspace/store";
import { FileTypeIcon } from "./file-icons";
import { DiffView } from "./diff-view";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  Menu,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tip,
} from "./ui/overlay";

/**
 * Interface dos chats de IA no padrão do Chat do VS Code (Copilot Chat e Claude Code): o estado
 * vazio com as ações sugeridas, a caixa do pedido com "Adicionar contexto…", o arquivo aberto e a
 * seleção como anexos, os seletores de modo e de modelo embaixo, as ferramentas e o enviar; na
 * resposta, quem respondeu e as ações; e a barra dos arquivos alterados (Manter / Desfazer).
 */

export const PROVIDER_NAME: Record<AiProvider, string> = { copilot: "GitHub Copilot", claude: "Claude" };

// ───────────── Estado vazio ─────────────

export function ChatWelcome({
  provider,
  title,
  link,
}: {
  provider: AiProvider;
  title: string;
  /** Ação de texto (ex.: "Gerar instruções do agente"), como o link do VS Code. */
  link?: { label: string; hint: string; onClick: () => void };
}) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-6 py-8 text-center">
      <span className="relative flex h-12 w-12 items-center justify-center">
        <MessageSquarePlus className="h-11 w-11 text-fg-muted" strokeWidth={1.25} />
        <ProviderMark provider={provider} className="absolute -right-1 -top-1 h-4 w-4" />
      </span>
      <p className="mt-4 text-[20px] font-semibold tracking-tight text-fg">{title}</p>
      <p className="mt-1.5 text-[12.5px] text-fg-subtle">As respostas da IA podem conter erros.</p>
      {link ? (
        <p className="mt-2 max-w-[240px] text-[12.5px] leading-relaxed text-fg-subtle">
          <button
            type="button"
            onClick={link.onClick}
            className="text-[var(--link,var(--fg))] hover:underline"
          >
            {link.label}
          </button>{" "}
          {link.hint}
        </p>
      ) : null}
    </div>
  );
}

/** "AÇÕES SUGERIDAS": botões acima da caixa do pedido (como no VS Code). */
export function SuggestedActions({ items, onPick }: { items: string[]; onPick: (text: string) => void }) {
  if (!items.length) return null;
  return (
    <div className="shrink-0 px-3 pb-2">
      <p className="mb-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-fg-subtle">
        Ações sugeridas
      </p>
      <div className="flex flex-wrap gap-1.5">
        {items.map((q) => (
          <button
            key={q}
            type="button"
            onClick={() => onPick(q)}
            className="rounded-md border border-border-strong px-2.5 py-1 text-left text-[12.5px] text-fg-muted transition-colors hover:bg-bg-muted hover:text-fg"
          >
            {q}
          </button>
        ))}
      </div>
    </div>
  );
}

// ───────────── Contexto (anexos do pedido) ─────────────

interface ContextState {
  /** Arquivos anexados com "Adicionar contexto…" (caminhos do projeto). */
  files: string[];
  /** O arquivo aberto vai junto (o olho do anexo, como no VS Code). */
  includeActive: boolean;
  /** A seleção do editor vai junto. */
  includeSelection: boolean;
  add(path: string): void;
  remove(path: string): void;
  clear(): void;
  set(patch: Partial<Pick<ContextState, "includeActive" | "includeSelection">>): void;
}

const makeContext = () =>
  create<ContextState>((set) => ({
    files: [],
    includeActive: true,
    includeSelection: true,
    add: (path) => set((s) => (s.files.includes(path) ? s : { files: [...s.files, path].slice(-12) })),
    remove: (path) => set((s) => ({ files: s.files.filter((f) => f !== path) })),
    clear: () => set({ files: [] }),
    set: (patch) => set(patch),
  }));

/** Contexto de cada chat (cada um lembra os seus anexos). */
export const chatContext: Record<AiProvider, ReturnType<typeof makeContext>> = {
  copilot: makeContext(),
  claude: makeContext(),
};

/** Arquivo aberto (caminho) e a seleção, como entram no pedido. */
export function currentEditorContext(provider: AiProvider): {
  activePath: string | null;
  selection: { text: string; from: number; to: number } | null;
} {
  const ctx = chatContext[provider].getState();
  const ws = activeWorkspace.current;
  const active = useWorkspace.getState().active;
  const activePath = ws && active ? (ws.session.tree().paths.get(active) ?? null) : null;
  const sel = activeEditor.current?.selection();
  let selection = null;
  if (ctx.includeSelection && sel && sel.text.trim()) {
    const doc = activeEditor.current!.view.state.doc;
    selection = { text: sel.text, from: doc.lineAt(sel.from).number, to: doc.lineAt(sel.to).number };
  }
  return { activePath: ctx.includeActive ? activePath : null, selection };
}

/** Texto dos arquivos anexados (para quem não lê o projeto sozinho, como o chat do Claude). */
export async function attachedFilesText(provider: AiProvider, maxChars = 60_000): Promise<string> {
  const ws = activeWorkspace.current;
  const { files } = chatContext[provider].getState();
  if (!ws || !files.length) return "";
  let budget = maxChars;
  const parts: string[] = [];
  for (const path of files) {
    const text = await readFile(ws.session, path).catch(() => null);
    if (text === null || budget <= 0) continue;
    const slice = text.slice(0, budget);
    budget -= slice.length;
    parts.push(`[Anexo: ${path}]\n\`\`\`\n${slice}${slice.length < text.length ? "\n…" : ""}\n\`\`\``);
  }
  return parts.length ? `${parts.join("\n\n")}\n\n` : "";
}

/** "Adicionar contexto…": escolhe arquivos do projeto (busca pelo nome ou pelo caminho). */
function AddContext({ provider, compact = false }: { provider: AiProvider; compact?: boolean }) {
  const tree = useTree();
  const add = chatContext[provider]((s) => s.add);
  const attached = chatContext[provider]((s) => s.files);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const files = useMemo(() => {
    const term = q.trim().toLowerCase();
    return [...tree.nodes.values()]
      .filter((n) => n.type === "file" && tree.paths.has(n.id))
      .map((n) => tree.paths.get(n.id)!)
      .filter((p) => !attached.includes(p) && (!term || p.toLowerCase().includes(term)))
      .sort((a, b) => a.length - b.length || a.localeCompare(b))
      .slice(0, 40);
  }, [tree, q, attached]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {compact ? (
          <button
            type="button"
            aria-label="Adicionar contexto"
            title="Adicionar contexto (arquivos do projeto)"
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-fg-muted transition-colors hover:bg-bg-muted hover:text-fg data-[state=open]:bg-bg-emphasis"
          >
            <Plus className="h-4 w-4" />
          </button>
        ) : (
          <button
            type="button"
            className="flex h-6 items-center gap-1 rounded-md border border-border px-1.5 text-[11.5px] text-fg-muted transition-colors hover:bg-bg-muted hover:text-fg data-[state=open]:bg-bg-emphasis"
          >
            <Paperclip className="h-3 w-3" /> Adicionar contexto…
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent align="start" side="top" className="w-80 p-0">
        <div className="relative border-b border-border p-2">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-subtle" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar arquivos do projeto"
            className="h-8 w-full rounded-md border border-border bg-bg pl-7 pr-2 text-[12.5px] text-fg placeholder:text-fg-subtle focus:border-fg-subtle focus:outline-none"
            aria-label="Buscar arquivos para anexar"
          />
        </div>
        <ul className="max-h-72 overflow-y-auto p-1">
          {files.map((p) => (
            <li key={p}>
              <button
                type="button"
                onClick={() => {
                  add(p);
                  setOpen(false);
                  setQ("");
                }}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px] text-fg-muted hover:bg-bg-muted hover:text-fg"
              >
                <FileTypeIcon name={p.split("/").pop()!} className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{p.split("/").pop()}</span>
                <span className="ml-auto truncate pl-2 text-[11px] text-fg-subtle">
                  {p.split("/").slice(0, -1).join("/")}
                </span>
              </button>
            </li>
          ))}
          {!files.length ? (
            <li className="px-3 py-4 text-center text-[12px] text-fg-subtle">Nada encontrado.</li>
          ) : null}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

function Chip({
  icon,
  label,
  title,
  dim,
  onToggle,
  onRemove,
  toggleLabel,
}: {
  icon: ReactNode;
  label: string;
  title?: string;
  dim?: boolean;
  onToggle?: () => void;
  onRemove?: () => void;
  toggleLabel?: string;
}) {
  return (
    <span
      title={title}
      className={cn(
        "flex h-6 max-w-[180px] items-center gap-1 rounded-md border border-border bg-bg-subtle pl-1.5 pr-0.5 text-[11.5px] text-fg",
        dim && "border-dashed text-fg-subtle",
      )}
    >
      {icon}
      <span className={cn("truncate", dim && "line-through")}>{label}</span>
      {onToggle ? (
        <button
          type="button"
          onClick={onToggle}
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-fg-subtle hover:bg-bg-emphasis hover:text-fg"
          aria-label={toggleLabel}
          title={toggleLabel}
        >
          {dim ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
        </button>
      ) : null}
      {onRemove ? (
        <button
          type="button"
          onClick={onRemove}
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-fg-subtle hover:bg-bg-emphasis hover:text-fg"
          aria-label={`Tirar ${label}`}
        >
          <X className="h-3 w-3" />
        </button>
      ) : null}
    </span>
  );
}

/** Linha de anexos: Adicionar contexto…, o arquivo aberto (com o olho), a seleção e os anexados. */
function ContextRow({ provider }: { provider: AiProvider }) {
  const ctx = chatContext[provider]();
  const tree = useTree();
  const active = useWorkspace((s) => s.active);
  // A seleção muda sem render: o cursor (Ln/Col) faz este componente atualizar.
  useWorkspace((s) => s.cursor);
  const activePath = active ? tree.paths.get(active) : undefined;
  const sel = activeEditor.current?.selection();
  const hasSelection = Boolean(sel && sel.text.trim());
  const lines = (() => {
    if (!hasSelection || !activeEditor.current) return "";
    const doc = activeEditor.current.view.state.doc;
    const a = doc.lineAt(sel!.from).number;
    const b = doc.lineAt(sel!.to).number;
    return a === b ? `:${a}` : `:${a}-${b}`;
  })();
  if (!activePath && !hasSelection && !ctx.files.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1 px-2 pt-2">
      {activePath ? (
        <Chip
          icon={<FileTypeIcon name={activePath.split("/").pop()!} className="h-3 w-3 shrink-0" />}
          label={`${activePath.split("/").pop()}${hasSelection && ctx.includeSelection ? lines : ""}`}
          title={`${activePath} — arquivo aberto${ctx.includeActive ? "" : " (não vai no pedido)"}`}
          dim={!ctx.includeActive}
          onToggle={() => ctx.set({ includeActive: !ctx.includeActive })}
          toggleLabel={ctx.includeActive ? "Não enviar o arquivo aberto" : "Enviar o arquivo aberto"}
        />
      ) : null}
      {hasSelection ? (
        <Chip
          icon={<TextSelect className="h-3 w-3 shrink-0" />}
          label="Seleção"
          dim={!ctx.includeSelection}
          onToggle={() => ctx.set({ includeSelection: !ctx.includeSelection })}
          toggleLabel={ctx.includeSelection ? "Não enviar a seleção" : "Enviar a seleção"}
        />
      ) : null}
      {ctx.files.map((p) => (
        <Chip
          key={p}
          icon={<FileTypeIcon name={p.split("/").pop()!} className="h-3 w-3 shrink-0" />}
          label={p.split("/").pop()!}
          title={p}
          onRemove={() => ctx.remove(p)}
        />
      ))}
    </div>
  );
}

// ───────────── Seletores (modo e modelo) ─────────────

export interface PickerOption {
  value: string;
  label: string;
  /** À direita (ex.: "1x" do Copilot, "Mais capaz"). */
  detail?: string;
  description?: string;
}

/** Seletor compacto embaixo da caixa ("Agente ▾", "Claude Sonnet 4.5 ▾"), como no VS Code. */
export function ChatPicker({
  label,
  value,
  options,
  onChange,
  title,
  icon,
}: {
  label: string;
  value: string;
  options: PickerOption[];
  onChange: (value: string) => void;
  title: string;
  icon?: ReactNode;
}) {
  const current = options.find((o) => o.value === value);
  return (
    <Menu>
      <Tip label={title}>
        <MenuTrigger asChild>
          <button
            type="button"
            className="flex h-6 min-w-0 max-w-[170px] items-center gap-1 rounded-md px-1.5 text-[12px] text-fg-muted transition-colors hover:bg-bg-muted hover:text-fg data-[state=open]:bg-bg-emphasis data-[state=open]:text-fg"
            aria-label={`${label}: ${current?.label ?? value}`}
          >
            {icon}
            <span className="truncate">{current?.label ?? value}</span>
            <ChevronDown className="h-3 w-3 shrink-0 opacity-70" />
          </button>
        </MenuTrigger>
      </Tip>
      <MenuContent align="start" side="top" className="max-h-[60vh] w-72 overflow-y-auto">
        <MenuLabel>{label}</MenuLabel>
        {options.map((o) => (
          <MenuItem
            key={o.value}
            className={o.description ? "h-auto items-start py-1.5" : undefined}
            onSelect={() => onChange(o.value)}
          >
            <Check className={cn("h-3.5 w-3.5 shrink-0", o.value === value ? "opacity-100" : "opacity-0")} />
            <span className="min-w-0 flex-1">
              <span className="block truncate">{o.label}</span>
              {o.description ? (
                <span className="block whitespace-normal text-[11px] leading-snug text-fg-subtle">
                  {o.description}
                </span>
              ) : null}
            </span>
            {o.detail ? <span className="shrink-0 text-[11px] text-fg-subtle">{o.detail}</span> : null}
          </MenuItem>
        ))}
      </MenuContent>
    </Menu>
  );
}

// ───────────── Seletor de modelo (como o do VS Code) ─────────────

export interface ModelChoiceItem {
  value: string;
  label: string;
  /** À direita: consumo ("1x", "0.33x") ou o perfil ("Mais capaz"). */
  detail?: string;
  description?: string;
}

export interface ModelPickerProps {
  provider: AiProvider;
  /** "Copilot", "Claude" (a aba do topo do seletor). */
  source: string;
  value: string;
  /** Valor do "Auto" (null: sem Auto). */
  autoValue: string | null;
  /** O que o Auto escolheu no último pedido (aparece ao lado). */
  autoDetail?: string;
  models: ModelChoiceItem[];
  /** Modelos que pedem um plano maior (botão "Upgrade"). */
  upgrade?: string[];
  onUpgrade?: () => void;
  onChange: (value: string) => void;
  /** Nível de raciocínio ("Inteligência"), se o modelo aceitar. */
  intelligence?: {
    value: string;
    options: Array<{ value: string; label: string }>;
    onChange(v: string): void;
  };
  /** Enquanto os modelos ainda não vieram (o chat está conectando). */
  loading?: ReactNode;
}

/**
 * Seletor de modelo no padrão do VS Code: a aba do provedor com a busca, o "Auto" com a
 * "Inteligência ›" (nível de raciocínio), os modelos do seu plano e, embaixo, os que pedem upgrade.
 */
export function ModelPicker(p: ModelPickerProps) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<"models" | "intelligence">("models");
  const [search, setSearch] = useState<string | null>(null);
  const isAuto = p.autoValue !== null && p.value === p.autoValue;
  const current = isAuto ? { label: "Auto" } : (p.models.find((m) => m.value === p.value) ?? null);
  const level = p.intelligence?.options.find((o) => o.value === p.intelligence!.value);
  const term = (search ?? "").trim().toLowerCase();
  const match = (label: string) => !term || label.toLowerCase().includes(term);
  const pick = (value: string) => {
    p.onChange(value);
    setOpen(false);
  };
  const row =
    "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12.5px] text-fg hover:bg-bg-muted";
  if (!current && !p.models.length && p.loading) return <>{p.loading}</>;
  const upgrade = (p.upgrade ?? []).filter(match);
  const models = p.models.filter((m) => match(m.label));
  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) {
          setView("models");
          setSearch(null);
        }
      }}
    >
      <Tip label="Modelo e inteligência">
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex h-6 min-w-0 max-w-[220px] items-center gap-1 rounded-md px-1.5 text-[12px] text-fg-muted transition-colors hover:bg-bg-muted hover:text-fg data-[state=open]:bg-bg-emphasis data-[state=open]:text-fg"
            aria-label={`Modelo: ${current?.label ?? p.value}${level ? ` · Inteligência ${level.label}` : ""}`}
          >
            <ProviderMark provider={p.provider} className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{current?.label ?? p.value}</span>
            {level ? (
              <span className="shrink-0 rounded bg-bg-emphasis px-1 text-[10.5px] text-fg-muted">
                {level.label}
              </span>
            ) : null}
          </button>
        </PopoverTrigger>
      </Tip>
      <PopoverContent side="top" align="start" className="w-[300px] p-0">
        {view === "intelligence" && p.intelligence ? (
          <div className="p-1">
            <button
              type="button"
              onClick={() => setView("models")}
              className="flex w-full items-center gap-1.5 px-2 py-1.5 text-[12px] font-medium text-fg-muted hover:text-fg"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Inteligência
            </button>
            <p className="px-2 pb-1.5 text-[11.5px] leading-snug text-fg-subtle">
              Quanto o modelo pensa antes de responder: mais inteligência dá respostas melhores, porém mais
              lentas.
            </p>
            {p.intelligence.options.map((o) => (
              <button
                key={o.value}
                type="button"
                className={row}
                onClick={() => {
                  p.intelligence!.onChange(o.value);
                  setView("models");
                }}
              >
                <Check
                  className={cn("h-3.5 w-3.5 shrink-0", o.value === p.intelligence!.value ? "" : "opacity-0")}
                />
                {o.label}
              </button>
            ))}
          </div>
        ) : (
          <>
            <div className="flex h-9 items-center gap-1 border-b border-border px-2">
              {search === null ? (
                <>
                  <span className="flex h-6 items-center gap-1.5 rounded-md bg-bg-emphasis px-2 text-[12px] font-medium text-fg">
                    <ProviderMark provider={p.provider} className="h-3.5 w-3.5" /> {p.source}
                  </span>
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className="ml-auto flex h-6 w-6 items-center justify-center rounded-md text-fg-subtle hover:bg-bg-muted hover:text-fg"
                    aria-label="Buscar modelo"
                  >
                    <Search className="h-3.5 w-3.5" />
                  </button>
                </>
              ) : (
                <input
                  autoFocus
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      e.stopPropagation();
                      setSearch(null);
                    }
                  }}
                  placeholder="Buscar modelo"
                  aria-label="Buscar modelo"
                  className="h-6 w-full rounded-md border border-border bg-bg px-2 text-[12px] text-fg placeholder:text-fg-subtle focus:outline-none"
                />
              )}
            </div>
            <div className="max-h-[52vh] overflow-y-auto p-1">
              {p.autoValue !== null && match("auto") ? (
                <div className={cn(row, "pr-1", isAuto && "bg-bg-muted")}>
                  <button
                    type="button"
                    onClick={() => pick(p.autoValue!)}
                    className="flex min-w-0 flex-1 items-center gap-2 text-left"
                  >
                    <Check className={cn("h-3.5 w-3.5 shrink-0", isAuto ? "" : "opacity-0")} />
                    <span className="shrink-0">Auto</span>
                    {p.autoDetail ? (
                      <span className="truncate text-[11px] text-fg-subtle">{p.autoDetail}</span>
                    ) : null}
                  </button>
                  {p.intelligence ? (
                    <button
                      type="button"
                      onClick={() => setView("intelligence")}
                      className="flex shrink-0 items-center gap-0.5 rounded px-1 text-[11.5px] text-fg-muted hover:text-fg"
                    >
                      Inteligência <ChevronRight className="h-3 w-3" />
                    </button>
                  ) : null}
                </div>
              ) : null}
              {models.length ? (
                <p className="px-2 pb-1 pt-2 text-[11px] font-semibold text-fg-muted">Modelos do seu plano</p>
              ) : null}
              {models.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => pick(m.value)}
                  className={cn(row, m.description && "items-start", m.value === p.value && "bg-bg-muted")}
                >
                  <Check
                    className={cn("mt-0.5 h-3.5 w-3.5 shrink-0", m.value === p.value ? "" : "opacity-0")}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{m.label}</span>
                    {m.description ? (
                      <span className="block text-[11px] leading-snug text-fg-subtle">{m.description}</span>
                    ) : null}
                  </span>
                  {m.detail ? <span className="shrink-0 text-[11px] text-fg-subtle">{m.detail}</span> : null}
                </button>
              ))}
              {p.intelligence && !isAuto && !term ? (
                <button type="button" onClick={() => setView("intelligence")} className={row}>
                  <span className="w-3.5 shrink-0" />
                  <span className="flex-1 text-fg-muted">Inteligência</span>
                  <span className="text-[11.5px] text-fg-subtle">{level?.label}</span>
                  <ChevronRight className="h-3 w-3 text-fg-subtle" />
                </button>
              ) : null}
              {upgrade.length ? (
                <>
                  <p className="px-2 pb-1 pt-2 text-[11px] font-semibold text-fg-muted">
                    Atualize o plano para mais modelos
                  </p>
                  {upgrade.map((name) => (
                    <button
                      key={name}
                      type="button"
                      onClick={() => {
                        setOpen(false);
                        p.onUpgrade?.();
                      }}
                      className={cn(row, "text-fg-muted")}
                    >
                      <span className="w-3.5 shrink-0" />
                      <span className="flex-1 truncate">{name}</span>
                      <span className="shrink-0 rounded-full bg-[color-mix(in_oklab,#d4a72c_32%,transparent)] px-1.5 text-[10.5px] font-semibold text-fg">
                        Upgrade
                      </span>
                    </button>
                  ))}
                </>
              ) : null}
              {term && !models.length && !upgrade.length && !match("auto") ? (
                <p className="px-2 py-3 text-center text-[12px] text-fg-subtle">Nenhum modelo encontrado.</p>
              ) : null}
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

/** Item da linha embaixo da caixa (conta, modo, permissões), como no Chat do VS Code. */
export function StatusChip({
  icon,
  label,
  title,
  children,
}: {
  icon?: ReactNode;
  label: string;
  title: string;
  /** Opções do menu do item (sem elas, o item é só informativo). */
  children?: ReactNode;
}) {
  const body = (
    <span className="flex min-w-0 items-center gap-1">
      {icon}
      <span className="truncate">{label}</span>
    </span>
  );
  if (!children)
    return (
      <Tip label={title}>
        <span className="flex h-6 min-w-0 items-center rounded-md px-1.5">{body}</span>
      </Tip>
    );
  return (
    <Menu>
      <Tip label={title}>
        <MenuTrigger asChild>
          <button
            type="button"
            className="flex h-6 min-w-0 items-center rounded-md px-1.5 transition-colors hover:bg-bg-muted hover:text-fg data-[state=open]:bg-bg-emphasis data-[state=open]:text-fg"
          >
            {body}
          </button>
        </MenuTrigger>
      </Tip>
      <MenuContent side="top" align="start" className="w-72">
        {children}
      </MenuContent>
    </Menu>
  );
}

/** Nomes dos níveis de raciocínio ("Inteligência"). */
export const INTELLIGENCE_LABEL: Record<string, string> = {
  none: "Mínima",
  low: "Baixa",
  medium: "Média",
  high: "Alta",
  xhigh: "Muito alta",
  max: "Máxima",
};

// ───────────── Caixa do pedido ─────────────

/**
 * Caixa do pedido no padrão do VS Code: anexos em cima, o texto, e embaixo os seletores, as
 * ferramentas e o enviar (ou parar). Enter envia; Shift+Enter quebra a linha.
 */
export function ChatInput({
  provider,
  value,
  onChange,
  onSubmit,
  onStop,
  busy,
  placeholder,
  pickers,
  tools,
  above,
  footer,
  inputRef,
}: {
  provider: AiProvider;
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onStop: () => void;
  busy: boolean;
  placeholder: string;
  pickers?: ReactNode;
  /** Botões à direita dos seletores (ferramentas, aprovação…). */
  tools?: ReactNode;
  /** Algo dentro da caixa, acima do texto (ex.: os campos de detalhar o pedido). */
  above?: ReactNode;
  /** Linha embaixo da caixa: a conta (e o plano), o modo e as permissões. */
  footer?: ReactNode;
  inputRef?: React.RefObject<HTMLTextAreaElement | null>;
}) {
  const local = useRef<HTMLTextAreaElement>(null);
  const ref = inputRef ?? local;
  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    if (!busy && value.trim()) onSubmit();
  };
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    }
  };
  return (
    <form onSubmit={submit} className="shrink-0 p-2 pt-1">
      <div className="rounded-lg border border-border-strong bg-bg-subtle focus-within:border-[var(--focus,var(--fg-subtle))]">
        {above}
        <ContextRow provider={provider} />
        <textarea
          ref={ref}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKey}
          rows={2}
          placeholder={placeholder}
          aria-label={`Pedido para o ${PROVIDER_NAME[provider]}`}
          className="block max-h-48 min-h-[44px] w-full resize-none bg-transparent px-2.5 py-2 text-[13px] leading-relaxed text-fg placeholder:text-fg-subtle focus:outline-none"
          style={{ fieldSizing: "content" } as React.CSSProperties}
        />
        <div className="flex items-center gap-0.5 px-1.5 pb-1.5">
          <AddContext provider={provider} compact />
          <div className="flex min-w-0 flex-1 items-center gap-0.5">{pickers}</div>
          {tools}
          {busy ? (
            <Tip label="Parar">
              <button
                type="button"
                onClick={onStop}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-fg text-fg-inverse hover:bg-fg/90"
                aria-label="Parar"
              >
                <Square className="h-3 w-3 fill-current" />
              </button>
            </Tip>
          ) : (
            <Tip label="Enviar (Enter)">
              <button
                type="submit"
                disabled={!value.trim()}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-fg text-fg-inverse transition-opacity hover:bg-fg/90 disabled:opacity-30"
                aria-label="Enviar"
              >
                <ArrowUp className="h-3.5 w-3.5" />
              </button>
            </Tip>
          )}
        </div>
      </div>
      {footer ? (
        <div className="flex min-w-0 items-center gap-0.5 px-0.5 pt-1 text-[11.5px] text-fg-subtle">
          {footer}
        </div>
      ) : null}
    </form>
  );
}

/** Botão de ícone da caixa do pedido (ferramentas, aprovação automática…). */
export function InputIconButton({
  label,
  pressed,
  onClick,
  children,
}: {
  label: string;
  pressed?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <Tip label={label}>
      <button
        type="button"
        onClick={onClick}
        aria-label={label}
        aria-pressed={pressed}
        className={cn(
          "flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-fg-subtle transition-colors hover:bg-bg-muted hover:text-fg",
          pressed && "bg-bg-emphasis text-fg",
        )}
      >
        {children}
      </button>
    </Tip>
  );
}

// ───────────── Conversa ─────────────

/** O pedido da pessoa (com os anexos que foram junto). */
export function RequestBubble({ text, context }: { text: string; context?: string[] }) {
  return (
    <div className="ml-6 flex flex-col items-end gap-1">
      {context?.length ? (
        <div className="flex flex-wrap justify-end gap-1">
          {context.map((c) => (
            <span
              key={c}
              className="flex items-center gap-1 rounded-md border border-border px-1.5 py-0.5 text-[11px] text-fg-subtle"
            >
              <FileIcon className="h-3 w-3" /> {c}
            </span>
          ))}
        </div>
      ) : null}
      <div className="whitespace-pre-wrap rounded-lg bg-bg-emphasis px-3 py-2 text-[13px] leading-relaxed text-fg">
        {text}
      </div>
    </div>
  );
}

/** Quem respondeu (ícone e nome), em cima da resposta. */
export function ResponseHeader({ provider }: { provider: AiProvider }) {
  return (
    <p className="mb-1.5 flex items-center gap-1.5 text-[12.5px] font-semibold text-fg">
      <span className="flex h-5 w-5 items-center justify-center rounded-full border border-border bg-bg">
        <ProviderMark provider={provider} className="h-3 w-3" />
      </span>
      {PROVIDER_NAME[provider]}
    </p>
  );
}

// ───────────── Arquivos alterados (Manter / Desfazer) ─────────────

export interface WorkingFile {
  path: string;
  /** Conteúdo antes da primeira alteração da IA (null: o arquivo não existia). */
  before: string | null;
  /** Conteúdo agora (null: apagado). */
  after: string | null;
}

/** Linhas acrescentadas e tiradas entre dois textos. */
export function diffStats(before: string | null, after: string | null): { added: number; removed: number } {
  let added = 0;
  let removed = 0;
  for (const part of diffLines(before ?? "", after ?? "")) {
    if (part.added) added += part.count ?? 0;
    else if (part.removed) removed += part.count ?? 0;
  }
  return { added, removed };
}

export function DiffCount({ before, after }: { before: string | null; after: string | null }) {
  const { added, removed } = useMemo(() => diffStats(before, after), [before, after]);
  return (
    <span className="shrink-0 font-mono text-[11px]">
      {added ? <span className="text-[var(--code-add,#4ade80)]">+{added}</span> : null}
      {added && removed ? " " : null}
      {removed ? <span className="text-[var(--code-error,#f87171)]">−{removed}</span> : null}
    </span>
  );
}

/** Abre o arquivo do projeto pelo caminho. */
export function openPath(path: string) {
  const ws = activeWorkspace.current;
  if (!ws) return;
  const id = [...ws.session.tree().paths].find(([, p]) => p === path)?.[0];
  if (id) openAt(id, 1);
}

/**
 * "N arquivos alterados · Manter · Desfazer" acima da caixa do pedido (como o Working Set do VS
 * Code): o que a IA mudou nesta conversa, com +/− por arquivo; Desfazer volta o conteúdo de antes.
 */
export function WorkingSetBar({
  files,
  onKeep,
  onUndo,
}: {
  files: WorkingFile[];
  onKeep: (path?: string) => void;
  onUndo: (path?: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [review, setReview] = useState<string | null>(null);
  if (!files.length) return null;
  const btn = "flex h-6 items-center gap-1 rounded-md px-2 text-[11.5px] font-medium transition-colors";
  return (
    <div className="mx-2 mb-1 overflow-hidden rounded-lg border border-border bg-bg-subtle">
      <div className="flex items-center gap-1 pl-1.5 pr-1 py-1">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex min-w-0 flex-1 items-center gap-1 text-left text-[12px] text-fg-muted hover:text-fg"
          aria-expanded={open}
        >
          <ChevronRight className={cn("h-3.5 w-3.5 shrink-0 transition-transform", open && "rotate-90")} />
          {files.length} {files.length === 1 ? "arquivo alterado" : "arquivos alterados"}
        </button>
        <button
          type="button"
          onClick={() => setReview(files[0]!.path)}
          className={cn(btn, "text-fg-muted hover:bg-bg-emphasis hover:text-fg")}
        >
          <FileDiff className="h-3 w-3" /> Revisar
        </button>
        <button
          type="button"
          onClick={() => onKeep()}
          className={cn(btn, "bg-fg text-fg-inverse hover:bg-fg/90")}
        >
          <Check className="h-3 w-3" /> Aprovar
        </button>
        <button
          type="button"
          onClick={() => onUndo()}
          className={cn(btn, "text-fg-muted hover:bg-bg-emphasis hover:text-fg")}
        >
          <Undo2 className="h-3 w-3" /> Desfazer
        </button>
      </div>
      {open ? (
        <ul className="max-h-40 overflow-y-auto border-t border-border py-1">
          {files.map((f) => (
            <li key={f.path} className="group flex items-center gap-1.5 px-2 py-0.5 text-[12px]">
              <FileTypeIcon name={f.path.split("/").pop()!} className="h-3.5 w-3.5 shrink-0" />
              <button
                type="button"
                onClick={() => openPath(f.path)}
                className={cn(
                  "min-w-0 truncate text-left text-fg hover:underline",
                  f.after === null && "line-through opacity-70",
                )}
                title={f.path}
              >
                {f.path.split("/").pop()}
              </button>
              <span className="min-w-0 truncate text-[11px] text-fg-subtle">
                {f.path.split("/").slice(0, -1).join("/")}
              </span>
              <span className="ml-auto" />
              <DiffCount before={f.before} after={f.after} />
              <Tip label="Ver o que mudou">
                <button
                  type="button"
                  onClick={() => setReview(f.path)}
                  className="hidden h-5 w-5 items-center justify-center rounded text-fg-subtle hover:bg-bg-emphasis hover:text-fg group-hover:flex"
                  aria-label={`Ver o que mudou em ${f.path}`}
                >
                  <FileDiff className="h-3 w-3" />
                </button>
              </Tip>
              <Tip label="Aprovar">
                <button
                  type="button"
                  onClick={() => onKeep(f.path)}
                  className="hidden h-5 w-5 items-center justify-center rounded text-fg-subtle hover:bg-bg-emphasis hover:text-fg group-hover:flex"
                  aria-label={`Manter ${f.path}`}
                >
                  <Check className="h-3 w-3" />
                </button>
              </Tip>
              <Tip label="Desfazer">
                <button
                  type="button"
                  onClick={() => onUndo(f.path)}
                  className="hidden h-5 w-5 items-center justify-center rounded text-fg-subtle hover:bg-bg-emphasis hover:text-fg group-hover:flex"
                  aria-label={`Desfazer ${f.path}`}
                >
                  <Undo2 className="h-3 w-3" />
                </button>
              </Tip>
            </li>
          ))}
        </ul>
      ) : null}
      <ChangesReview
        files={files}
        path={review}
        onPath={setReview}
        onKeep={(p) => {
          onKeep(p);
          if (!p) setReview(null);
        }}
        onUndo={(p) => {
          onUndo(p);
          if (!p) setReview(null);
        }}
      />
    </div>
  );
}

/**
 * Revisão das mudanças da IA: a lista de arquivos à esquerda e o diff do escolhido à direita, com
 * Aprovar e Desfazer por arquivo ou tudo de uma vez (como a revisão de mudanças do VS Code).
 */
export function ChangesReview({
  files,
  path,
  onPath,
  onKeep,
  onUndo,
}: {
  files: WorkingFile[];
  path: string | null;
  onPath: (path: string | null) => void;
  onKeep: (path?: string) => void;
  onUndo: (path?: string) => void;
}) {
  const current = files.find((f) => f.path === path) ?? files[0] ?? null;
  const patch = useMemo(
    () =>
      current
        ? createPatch(current.path, current.before ?? "", current.after ?? "", "antes", "depois", {
            context: 3,
          })
        : "",
    [current],
  );
  const next = (gone: string) => {
    const rest = files.filter((f) => f.path !== gone);
    onPath(rest[0]?.path ?? null);
  };
  return (
    <Dialog open={path !== null && files.length > 0} onOpenChange={(o) => !o && onPath(null)}>
      <DialogContent
        title="Revisar as mudanças da IA"
        description={`${files.length} ${files.length === 1 ? "arquivo alterado" : "arquivos alterados"}. Aprove o que fica e desfaça o que não quer.`}
        className="max-w-[1100px]"
      >
        <div className="grid h-[min(70vh,640px)] grid-cols-[240px_minmax(0,1fr)] gap-3">
          <ul className="overflow-y-auto rounded-lg border border-border bg-bg-subtle py-1">
            {files.map((f) => (
              <li key={f.path}>
                <button
                  type="button"
                  onClick={() => onPath(f.path)}
                  className={cn(
                    "flex w-full items-center gap-1.5 px-2 py-1.5 text-left text-[12px] hover:bg-bg-muted",
                    current?.path === f.path && "bg-bg-emphasis",
                  )}
                  title={f.path}
                >
                  <FileTypeIcon name={f.path.split("/").pop()!} className="h-3.5 w-3.5 shrink-0" />
                  <span
                    className={cn("min-w-0 flex-1 truncate", f.after === null && "line-through opacity-70")}
                  >
                    {f.path.split("/").pop()}
                  </span>
                  <DiffCount before={f.before} after={f.after} />
                </button>
              </li>
            ))}
          </ul>
          {current ? (
            <div className="flex min-h-0 flex-col gap-2">
              <div className="flex items-center gap-2">
                <p
                  className="min-w-0 flex-1 truncate font-mono text-[12px] text-fg-muted"
                  title={current.path}
                >
                  {current.path}
                  {current.before === null ? " · arquivo novo" : current.after === null ? " · apagado" : ""}
                </p>
                <Button variant="ghost" size="sm" onClick={() => openPath(current.path)}>
                  Abrir
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    onUndo(current.path);
                    next(current.path);
                  }}
                >
                  <Undo2 className="h-3 w-3" /> Desfazer
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    onKeep(current.path);
                    next(current.path);
                  }}
                >
                  <Check className="h-3 w-3" /> Aprovar
                </Button>
              </div>
              <DiffView patch={patch} className="min-h-0 flex-1" />
              <div className="flex justify-end gap-2 border-t border-border pt-2">
                <Button variant="ghost" size="sm" onClick={() => onUndo()}>
                  Desfazer tudo
                </Button>
                <Button variant="primary" size="sm" onClick={() => onKeep()}>
                  Aprovar tudo
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
